import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Image as ImageIcon } from "lucide-react";
import { productImgSrc } from "@/pages/Products";

/**
 * Create / edit dialog. Uses multipart so the cover image (optional) can ride
 * along in the same POST/PATCH call.
 */
export default function ProductDialog({ open, onOpenChange, product, onSaved, existingCategories = [] }) {
  const isEdit = !!product?.id;
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [categoryVal, setCategoryVal] = useState("");
  const [image, setImage] = useState(null);
  const [preview, setPreview] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setName(product?.name || "");
    setDescription(product?.description || "");
    setCategoryVal(product?.category || "");
    setImage(null);
    setPreview(product?.image_url ? productImgSrc(product.image_url) : "");
  }, [open, product?.id]);  // eslint-disable-line react-hooks/exhaustive-deps

  const onFile = (e) => {
    const f = e.target.files?.[0];
    setImage(f || null);
    if (f) setPreview(URL.createObjectURL(f));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim()) { toast.error("Name is required"); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("name", name.trim());
      fd.append("description", description || "");
      fd.append("category", categoryVal.trim());
      if (image) fd.append("image", image);
      if (isEdit) {
        await api.patch(`/products/${product.id}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
        toast.success("Product updated");
      } else {
        await api.post(`/products`, fd, { headers: { "Content-Type": "multipart/form-data" } });
        toast.success("Product created");
      }
      onSaved?.();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="product-dialog">
        <DialogHeader>
          <DialogTitle>{isEdit ? "Edit product" : "New product"}</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <Label>Product name *</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} required data-testid="product-name-input" />
          </div>
          <div>
            <Label>Category</Label>
            <Input
              value={categoryVal}
              onChange={(e) => setCategoryVal(e.target.value)}
              placeholder="e.g. Cloud, Security, Devices"
              list="product-category-suggest"
              data-testid="product-category-input"
            />
            <datalist id="product-category-suggest">
              {existingCategories.map((c) => <option key={c} value={c} />)}
            </datalist>
            <p className="text-[11px] text-slate-500 mt-1">Group similar products together. Pick an existing one or type a new name.</p>
          </div>
          <div>
            <Label>Description</Label>
            <Textarea
              rows={4}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Short overview shown on the product page"
              data-testid="product-description-input"
            />
          </div>
          <div>
            <Label>Cover image (PNG / JPG / WebP, ≤5MB)</Label>
            <div className="mt-1.5 flex items-start gap-3">
              <label className="w-24 h-24 border-2 border-dashed border-slate-200 hover:border-[#1D63ED]/60 rounded-lg flex items-center justify-center cursor-pointer overflow-hidden bg-slate-50 flex-shrink-0">
                {preview
                  ? <img src={preview} alt="preview" className="w-full h-full object-cover" />
                  : <ImageIcon className="w-6 h-6 text-slate-400" />}
                <input type="file" accept="image/*" onChange={onFile} className="hidden" data-testid="product-image-input" />
              </label>
              <p className="text-xs text-slate-500 mt-1">Optional. Click the tile to pick a cover.</p>
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)} disabled={busy}>Cancel</Button>
            <Button type="submit" className="bg-[#1D63ED] hover:bg-[#1854D9] text-white" disabled={busy} data-testid="product-save-btn">
              {busy ? "Saving…" : (isEdit ? "Save changes" : "Create product")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
